import React from 'react';
import { useGetPermissions } from '../../../../Permissions/hooks/useGetPermissions';
import { Box, Button, Flexbox, Icon, Modal, Select, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { ErrorResponse } from '../../../../../common/types/errorRespose';
import { Spinner } from '../../../../../common/components/Spinner/Spinner';
import { Controller } from 'react-hook-form';
import { useAddPermissionsToCustomRoleForm } from '../../../hooks/useAddPermissionsToCustomRoleForm';
import styled from 'styled-components';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { CustomRolePermissionDtoWithoutAliasDto } from '../../../../../api-client';

type AddPermissionToCustomRoleModalProps = {
  onClose: () => void;
  isOpen: boolean;
  isAdding: boolean;
  error: ErrorResponse | null;
  addPermissionToCustomRole: (permissions: CustomRolePermissionDtoWithoutAliasDto[]) => void;
  permissionsToFilterOut: string[];
};

export type AddPermissionToCustomRoleModalComponent = React.FC<AddPermissionToCustomRoleModalProps>;

const StyledModal = styled(Modal)`
  height: 70vh;

  > div:nth-child(3) {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

export const AddPermissionToCustomRoleModal: AddPermissionToCustomRoleModalComponent = ({
  onClose,
  isOpen,
  isAdding,
  error,
  addPermissionToCustomRole,
  permissionsToFilterOut,
}) => {
  const {
    isLoading: isLoadingPermissions,
    data: permissions,
    error: getPermissionsError,
  } = useGetPermissions({
    cacheTime: 50000,
    staleTime: Infinity,
  });
  const { control, handleSubmit, formState } = useAddPermissionsToCustomRoleForm();

  const getSelectOptions = () => {
    if (permissions) {
      return permissions
        .filter((permission) => !permissionsToFilterOut.includes(permission.id))
        .map((permission) => ({
          label: `${permission.id} ${permission.description ? `(${permission.description})` : ''}`,
          value: permission.id,
        }));
    } else {
      return [];
    }
  };

  const onFormSubmit = (data: { permissions: Option[] }) => {
    const permissionsToAdd = data.permissions.map((option) => ({
      id: option.value.toString(),
    }));
    addPermissionToCustomRole(permissionsToAdd);
  };

  return (
    <StyledModal
      data-testid="add-permission-to-custom-role-modal"
      title="Add permission"
      onClose={onClose}
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            data-testid="add-permissions-btn"
            type="submit"
            // @ts-ignore
            onClick={handleSubmit(onFormSubmit)}
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 1 ||
              isAdding
            }
          >
            {isAdding ? 'Adding ...' : 'Add'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={getPermissionsError as ErrorResponse} />
      <ErrorBox error={error} />
      <Flexbox container gutter={4} alignItems="flex-start">
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container>
          <Typography variant="body2">
            Select permissions to add from the list below.
            <br />
            You can add attributes to the selected permissions later.
          </Typography>
        </Flexbox>
      </Flexbox>
      <Flexbox container flex="1 1 auto">
        <form style={{ width: '100%' }}>
          {isLoadingPermissions && !isAdding ? (
            <Flexbox
              item
              container
              width="100%"
              height="100%"
              justifyContent="center"
              alignItems="center"
            >
              <Spinner />
            </Flexbox>
          ) : (
            <Box my={4}>
              <Controller
                name="permissions"
                defaultValue=""
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Select
                    fullWidth
                    label="Permissions"
                    isMulti={true}
                    isClearable={false}
                    name="permissions"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                    options={getSelectOptions()}
                  />
                )}
              />
            </Box>
          )}
        </form>
      </Flexbox>
    </StyledModal>
  );
};
